import { useState } from 'react';
import { inquiryAPI } from '../../../services/api';

// Pre-job customer intake.
//
// #52 / #96 scope rule: `setIsLoading` is the orchestrator's SHARED flag, and
// it reaches DataTable as `loading`. Flipping it swaps the table out and
// unmounts whatever row button opened an overlay, leaving Modal.js with no
// live node to restore focus to. Only list-level fetches may use it.
// Everything triggered from inside an overlay -- or from a row action, whose
// trigger lives in the table -- uses `overlayLoading` instead.
export const useInquiries = ({ setIsLoading }) => {
  const [inquiries, setInquiries] = useState([]);
  const [inquiryStatus, setStatus] = useState('open');
  const [inquiriesPage, setInquiriesPage] = useState(1);
  const [inquiriesTotalPages, setInquiriesTotalPages] = useState(1);
  const [overlayLoading, setOverlayLoading] = useState(false);

  // Changing the filter always returns to page 1. Statuses have wildly
  // different row counts -- a shop with three pages of open inquiries has one
  // page of closed ones -- so carrying the page across a switch lands on an
  // empty table. Worse, the pager only renders when there is more than one
  // page, so the Previous button that would get the user back is not there
  // either: the filter strands them with no way out but a reload.
  const setInquiryStatus = (status) => {
    setStatus(status);
    setInquiriesPage(1);
  };

  // A list-level fetch: the shared flag is correct here, and the complement
  // test asserts it, so the scope rule does not over-correct into a list that
  // never shows loading at all.
  const loadInquiries = async () => {
    try {
      setIsLoading(true);
      const data = await inquiryAPI.list({
        status: inquiryStatus,
        page: inquiriesPage,
        page_size: 20,
      });
      setInquiries(data.inquiries || []);
      setInquiriesTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error loading inquiries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Runs from inside the quick-add overlay.
  const createInquiry = async (payload) => {
    try {
      setOverlayLoading(true);
      const created = await inquiryAPI.create(payload);
      await refresh();
      return created;
    } finally {
      setOverlayLoading(false);
    }
  };

  // Runs from inside the convert overlay, when the mechanic corrected one of
  // the carried fields before opening the job. Same scope rule as its
  // siblings: the overlay flag, never the shared one, and the rejection
  // propagates so the modal can show it and decline to convert.
  const updateInquiry = async (inquiryId, payload) => {
    try {
      setOverlayLoading(true);
      const updated = await inquiryAPI.update(inquiryId, payload);
      await refresh();
      return updated;
    } finally {
      setOverlayLoading(false);
    }
  };

  // Runs from inside the convert overlay.
  const convertInquiry = async (inquiryId, payload) => {
    try {
      setOverlayLoading(true);
      const job = await inquiryAPI.convert(inquiryId, payload);
      await refresh();
      return job;
    } finally {
      setOverlayLoading(false);
    }
  };

  // A row action: its trigger lives inside the table, so the shared flag
  // would unmount the button mid-click.
  const closeInquiry = async (inquiryId) => {
    try {
      setOverlayLoading(true);
      const closed = await inquiryAPI.close(inquiryId);
      await refresh();
      return closed;
    } finally {
      setOverlayLoading(false);
    }
  };

  // Re-read the list WITHOUT the shared flag, for use after a mutation that
  // ran from an overlay. Calling loadInquiries here would defeat the rule.
  const refresh = async () => {
    try {
      const data = await inquiryAPI.list({
        status: inquiryStatus,
        page: inquiriesPage,
        page_size: 20,
      });
      setInquiries(data.inquiries || []);
      setInquiriesTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error refreshing inquiries:', error);
    }
  };

  return {
    inquiries,
    inquiryStatus,
    setInquiryStatus,
    inquiriesPage,
    setInquiriesPage,
    inquiriesTotalPages,
    overlayLoading,
    loadInquiries,
    createInquiry,
    updateInquiry,
    convertInquiry,
    closeInquiry,
  };
};
